import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import HotelIcon from '@mui/icons-material/Hotel';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import SchoolIcon from '@mui/icons-material/School';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import { providerApi } from '../../services/api';
import { formatServicePrice, getCurrencySymbol, PROVIDER_SERVICE_CURRENCY_CODE } from '../../utils/currencyUtils';
import { useAuth } from '../../contexts/AuthContext';

// Predefined dog service types from backend (label + icon for display)
const DOG_SERVICE_TYPES = [
  { value: 'DOG_WALKING', label: 'Dog Walking', Icon: DirectionsWalkIcon },
  { value: 'BOARDING', label: 'Boarding', Icon: HotelIcon },
  { value: 'HOUSE_SITTING', label: 'Sitting', Icon: HomeIcon },
  { value: 'DROP_IN_VISITS', label: 'Visits', Icon: MeetingRoomIcon },
  { value: 'DAY_CARE', label: 'Day Care', Icon: ChildFriendlyIcon },
  { value: 'TRAINING', label: 'Training', Icon: SchoolIcon },
  { value: 'GROOMING', label: 'Grooming', Icon: ContentCutIcon },
];

const getServiceMeta = (type) => DOG_SERVICE_TYPES.find((entry) => entry.value === type) || { label: type, Icon: RoomServiceIcon };

// ── Section shell (matches the dashboard's border-based card style) ──────────
function Section({ title, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2.5, ...sx }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, minHeight: 32 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.68rem' }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

const ServiceManagement = ({ providerData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { refreshProviderData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [services, setServices] = useState(providerData.services || []);
  const [newService, setNewService] = useState({ dogServiceType: '', description: '', price: '' });
  const resolvedCurrencyCode = PROVIDER_SERVICE_CURRENCY_CODE;

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setServices(providerData.services || []);
    setNewService({ dogServiceType: '', description: '', price: '' });
    setError('');
    setSuccess('');
  };

  const handleAddService = () => {
    if (newService.dogServiceType && newService.price && resolvedCurrencyCode) {
      setServices([...services, { ...newService, currencyCode: resolvedCurrencyCode, id: `temp-${Date.now()}` }]);
      setNewService({ dogServiceType: '', description: '', price: '' });
    }
  };

  const handleRemoveService = (serviceId) => {
    setServices(services.filter(service => service.id !== serviceId));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      // Fold in a pending entry from the "Add New Service" form so the user doesn't
      // silently lose it by clicking Save without first clicking Add.
      const hasPending = newService.dogServiceType && newService.price;
      const servicesToSave = hasPending
        ? [...services, { ...newService, currencyCode: resolvedCurrencyCode, id: `temp-${Date.now()}` }]
        : services;

      // PUT /providers/me/services is a full replace of the catalogue and takes exactly these
      // four fields per row -- currencyCode and providerId are not part of the request shape,
      // because the server stamps the marketplace currency itself (that is what repairs rows
      // still carrying a pre-V114 USD).
      //
      // New rows carry a client-side temp id (e.g. "temp-123") for React keys; the server wants
      // null there and inserts. An existing row MUST keep its real id: orders reference
      // provider_services.id, so a kept row has to be updated in place, not re-created.
      const payload = servicesToSave.map((service) => {
        const isTempId = typeof service.id === 'string' && service.id.startsWith('temp-');
        return {
          id: isTempId ? null : service.id ?? null,
          dogServiceType: service.dogServiceType,
          price: service.price !== '' && service.price != null ? parseFloat(service.price) : service.price,
          description: service.description || null,
        };
      });

      await providerApi.updateProviderServices(payload);

      // Refresh shared data in place (no full reload) and re-sync the local list
      // so newly-added services pick up their real server-assigned ids.
      const fresh = await refreshProviderData();
      if (fresh?.services) {
        setServices(fresh.services);
      }
      setNewService({ dogServiceType: '', description: '', price: '' });

      setSuccess(t('services.updated'));
      setIsEditing(false);
      setIsSaving(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('services.updateFailed'));
      setIsSaving(false);
    }
  };

  const canAdd = newService.dogServiceType && newService.price && resolvedCurrencyCode;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('services.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('services.subtitle')}
          </Typography>
        </Box>
        {!isEditing && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
            {t('services.editButton')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2.5 }}>{success}</Alert>}
      {/* Services list */}
      <Section title={services.length ? `${t('services.offeredTitle')} · ${services.length}` : t('services.offeredTitle')}>
        {services.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
            <RoomServiceIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
            <Typography variant="body2">
              {isEditing ? t('services.emptyEditing') : t('services.emptyIdle')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
            {services.map((service) => {
              const { Icon } = getServiceMeta(service.dogServiceType);
              const label = t('services.type' + service.dogServiceType, { defaultValue: service.dogServiceType });
              return (
                <Paper
                  key={service.id}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    gap: 1.5,
                    position: 'relative',
                  }}
                >
                  <Avatar sx={{ bgcolor: alpha(primary, 0.1), color: primary, width: 42, height: 42, borderRadius: 1.5, flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, wordBreak: 'break-word' }}>
                      {service.description || t('services.noDescription')}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="primary">
                      {formatServicePrice({
                        ...service,
                        currencyCode: resolvedCurrencyCode,
                      })}
                    </Typography>
                  </Box>
                  {isEditing && (
                    <Tooltip title={t('services.removeTooltip')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveService(service.id)}
                        sx={{ position: 'absolute', top: 6, right: 6 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </Section>

      {/* Add service form */}
      {isEditing && (
        <Section title={t('services.addTitle')}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'start',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.1fr 1.5fr 1fr auto' },
            }}
          >
            <FormControl fullWidth>
              <InputLabel>{t('services.serviceType')}</InputLabel>
              <Select
                value={newService.dogServiceType}
                label={t('services.serviceType')}
                onChange={(e) => setNewService({ ...newService, dogServiceType: e.target.value })}
              >
                {DOG_SERVICE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {t('services.type' + type.value, { defaultValue: type.label })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('services.description')}
              fullWidth
              value={newService.description}
              onChange={(e) => setNewService({ ...newService, description: e.target.value })}
            />
            <TextField
              label={t('services.price')}
              type="number"
              fullWidth
              value={newService.price}
              onChange={(e) => setNewService({ ...newService, price: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {getCurrencySymbol(resolvedCurrencyCode)}
                  </InputAdornment>
                ),
              }}
              helperText={t('services.priceHelper')}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddService}
              disabled={!canAdd}
              sx={{ height: 56, whiteSpace: 'nowrap' }}
            >
              {t('services.addButton')}
            </Button>
          </Box>
        </Section>
      )}

      {/* Save / cancel */}
      {isEditing && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
          >
            {t('services.saveButton')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ServiceManagement;
